import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, TriangleAlert, X } from "lucide-react";

import { apiGet } from "../Api/Client.js";
import { isCompleted } from "./ScreenUtils.jsx";

export function Panel({ title, subtitle, right, children, className = "" }) {
  return <section className={`Panel Glass Fade-In ${className}`}><header><div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>{right}</header><div>{children}</div></section>;
}

export function Disclosure({ title, subtitle, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const onHeaderKey = (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setOpen(!open); }
  };
  return <section className="Panel Disclosure Glass Fade-In"><header role="button" tabIndex={0} aria-expanded={open} onClick={() => setOpen(!open)} onKeyDown={onHeaderKey} style={{ cursor: "pointer" }}><div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>{open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</header>{open && <div>{children}</div>}</section>;
}

// A dead GitHub token used to read as "nothing found" — search came back
// empty, and there was no way to tell that apart from "this repo genuinely
// doesn't exist" (2026-08-19: exactly this confused a real unlink into
// looking like data loss). This is the one place that difference gets
// surfaced, same visual weight as the "viewing as" impersonation bar so it
// reads as "something is actually wrong," not routine page furniture.
// Polls on its own — no page that renders this needs to know or care when
// the integration comes back; the bar just clears itself.
export function IntegrationHealthBanner() {
  const [github, setGithub] = useState(null);

  useEffect(() => {
    let alive = true;
    const check = () => apiGet("/Project/integration-health/")
      .then((res) => { if (alive) setGithub(res.github); })
      .catch(() => {});
    check();
    const interval = setInterval(check, 60000);
    return () => { alive = false; clearInterval(interval); };
  }, []);

  // A company that has not connected GitHub is not having an outage: the repository panel offers
  // "Connect GitHub" instead, so the red bar is only for a connection that stopped working.
  if (!github || github.ok || github.connected === false) return null;
  return (
    <div className="Integration-Down-Bar">
      <TriangleAlert size={15} />
      <span>GitHub isn't reachable right now — repo search, linking, and access syncing won't work until this is fixed.</span>
    </div>
  );
}

// Same pattern as IntegrationHealthBanner above, for the Slack bot that
// posts Meet Recorder meeting summaries into the sales channel. The check
// behind /Project/integration-health-slack/ only actually runs once an
// hour (a scheduled heartbeat, not this poll) — polling every 60s here just
// picks up that shared cached answer quickly, it never calls Slack itself.
export function SlackIntegrationHealthBanner() {
  const [slack, setSlack] = useState(null);

  useEffect(() => {
    let alive = true;
    const check = () => apiGet("/Project/integration-health-slack/")
      .then((res) => { if (alive) setSlack(res.slack); })
      .catch(() => {});
    check();
    const interval = setInterval(check, 60000);
    return () => { alive = false; clearInterval(interval); };
  }, []);

  if (!slack || slack.ok) return null;
  return (
    <div className="Integration-Down-Bar">
      <TriangleAlert size={15} />
      <span>Slack isn't reachable right now — meeting summaries from Meet Recorder won't post to the sales channel until this is fixed.</span>
    </div>
  );
}

// Avatar + name + department-or-exited, the identity portion of a team row.
// A Fragment, not a wrapping element, so it drops directly into whatever grid
// the caller already has going — Project Management's row appends a repo-
// access count and a remove button after it; the Projects page's read-only
// board aside appends nothing. Before this existed, the board's version had
// fallen behind: it still showed a raw role label ("Member", "Stakeholder")
// where Project Management had already moved to department + a blue name
// for full-timers, because the fix landed in only one of the two places a
// team gets shown.
export function TeamIdentity({ member }) {
  const exited = member.status === "Exited";
  return (
    <>
      <span className="Sanity-Avatar">{member.initials}</span>
      <span className={"Team-Name" + (exited ? " exited" : member.is_full_time ? " full-time" : "")}>
        {member.display_name}
      </span>
      <span className="Team-Dept">{exited ? "exited" : member.department || "—"}</span>
    </>
  );
}

// The points-done/points-total bar + counts, shared by the compact sprint
// strip on Projects and the full sprint block on Project Management. Both
// screens used to compute the same percentage and render the same three
// lines themselves — same CSS classes (.Release-Bar/-Points/-Tasks) either
// way, so there was nothing screen-specific left to justify two copies.
export function SprintProgress({ sprint }) {
  const pct = sprint.points_total > 0 ? Math.round((sprint.points_done / sprint.points_total) * 100) : 0;
  return (
    <div className="Release-Foot">
      <div className="Release-Bar"><span style={{ width: `${pct}%` }} /></div>
      <span className="Release-Points">{sprint.points_done}/{sprint.points_total} pts</span>
      <span className="Release-Tasks">{sprint.task_done_count}/{sprint.task_count} tasks</span>
      {/* Appended, never inserted — a trailing element only shows up when
         there's something to say (overdue_task_count > 0) and never moves
         what's already here, the same "don't disturb the row" rule the
         contributor chips next to it already follow. */}
      {sprint.overdue_task_count > 0 && (
        <span className="Release-Overdue">{sprint.overdue_task_count} overdue</span>
      )}
    </div>
  );
}

// First name only, everywhere — except the rare case where two people on
// this exact project share a first name, where both of THEM (not everyone
// else) get their full name instead so they stay distinguishable. Never
// initials — shared by task-owner labels and the sprint contributor chips
// below, so the same person reads the same way wherever their name shows up
// on a given project.
export function ownerLabelsForTeam(team) {
  const firstNameCounts = {};
  for (const m of team || []) {
    const first = (m.display_name || "").trim().split(/\s+/)[0];
    if (first) firstNameCounts[first] = (firstNameCounts[first] || 0) + 1;
  }
  const labels = {};
  for (const m of team || []) {
    const name = m.display_name || "";
    const first = name.trim().split(/\s+/)[0];
    labels[name] = first && firstNameCounts[first] > 1 ? name : (first || name);
  }
  return labels;
}

// Who has a task in this sprint — placed in whatever room a sprint row
// already had free rather than earning its own reserved column, per direct
// instruction not to reshuffle anything else on the row. Most sprints have
// one or two people; past that, the rest fold into a "+N" chip whose hover
// panel lists them (same Team-Repo-Wrap/Repo-Hover-Panel pattern already
// used for repo collaborators), so a big sprint like Kavach doesn't spill
// five chips across the row.
export function ContributorChips({ contributors, team, max = 2 }) {
  if (!contributors?.length) return null;
  const labels = ownerLabelsForTeam(team);
  const named = contributors.map((c) => labels[c.display_name] || c.display_name);
  const visible = named.slice(0, max);
  const overflow = named.slice(max);
  return (
    <span className="Contributor-Chips">
      {visible.map((label) => <span key={label} className="Contributor-Chip">{label}</span>)}
      {overflow.length > 0 && (
        <span className="Team-Repo-Wrap Contributor-Chip-Overflow-Wrap">
          <span className="Contributor-Chip Contributor-Chip-Overflow">+{overflow.length}</span>
          <div className="Repo-Hover-Panel">
            <p className="Repo-Hover-Title">Also on this sprint</p>
            {overflow.map((name) => <div key={name} className="Repo-Hover-Item">{name}</div>)}
          </div>
        </span>
      )}
    </span>
  );
}

// One collapse pattern, shared by Projects' Team/Repositories aside and
// Project Management's Closed Sprints — neither is daily reading, so both
// start closed, with the toggle (count + chevron) sitting on the label's own
// right edge rather than a separate control underneath.
export function Fold({ label, count, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="Exec-Fold">
      <button type="button" className="Exec-Fold-Head" onClick={() => setOpen(!open)}>
        <span className="Exec-Fold-Label">{label}</span>
        <span className="Exec-Fold-Toggle">{count}<ChevronDown size={13} className={open ? "rot" : ""} /></span>
      </button>
      {open && <div className="Exec-Fold-Body">{children}</div>}
    </div>
  );
}

export function Tabs({ value, onChange, items = [] }) {
  return <div className="Tabs">{items.map(([id, label]) => <button key={id} className={value === id ? "Active" : ""} onClick={() => onChange(id)}>{label}</button>)}</div>;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({ title, children, onClose, wide, closable = true }) {
  const dialogRef = useRef(null);
  useEffect(() => {
    if (!closable) return undefined;
    const onKey = (event) => { if (event.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, closable]);
  // Move focus into the dialog when it opens (unless a field inside already took it)
  // and hand it back to whatever opened the dialog when it closes.
  useEffect(() => {
    const opener = document.activeElement;
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) dialog.focus();
    return () => { if (opener && opener.isConnected) opener.focus?.(); };
  }, []);
  // Keep Tab / Shift+Tab inside the dialog.
  const keepFocusInside = (event) => {
    if (event.key !== "Tab") return;
    const items = [...dialogRef.current.querySelectorAll(FOCUSABLE)];
    if (!items.length) { event.preventDefault(); return; }
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };
  return (
    <div className="Modal-Backdrop">
      <section ref={dialogRef} role="dialog" aria-modal="true" aria-label={typeof title === "string" ? title : undefined} tabIndex={-1} onKeyDown={keepFocusInside} style={{ outline: "none" }} className={wide ? "Modal Wide" : "Modal"}>
        <header>
          <h1>{title}</h1>
          {closable && <button className="Modal-Close-Btn" onClick={onClose} title="Close" aria-label="Close"><X size={18} /></button>}
        </header>
        <div className="Modal-Body">{children}</div>
      </section>
    </div>
  );
}

export function StatCard({ label, value, icon }) {
  return <section className="Stat-Card">{icon && <div className="Stat-Icon">{icon}</div>}<div><span>{label}</span><strong>{value}</strong></div></section>;
}

export function StatusPill({ children, tone = "Neutral" }) {
  const toneMap = {
    neutral: "Slate", green: "Green", red: "Red", gold: "Gold", blue: "Blue",
    slate: "Slate", Neutral: "Slate", Green: "Green", Red: "Red", Gold: "Gold", Blue: "Blue", Slate: "Slate",
    info: "Blue", warning: "Gold", error: "Red", success: "Green",
  };
  const cssTone = toneMap[tone] || "Slate";
  return <span className={`Status-Pill ${cssTone}`}>{children}</span>;
}

export function EmptyState({ label }) {
  return <div className="Empty-State">{label}</div>;
}

export function SimpleTable({ columns = [], rows = [] }) {
  return (
    <div className="Table-Wrap">
      <table className="Erp-Table">
        <thead><tr>{columns.map((column) => <th key={column}>{column}</th>)}</tr></thead>
        <tbody>{rows.map((row, index) => <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

export function MilestoneRail({ milestones = [] }) {
  if (!milestones.length) return null;
  return <div className="Milestone-Rail">{milestones.slice(0, 12).map((milestone) => <span key={milestone.id} className={isCompleted(milestone.status) ? "done" : milestone.status === "Delayed" ? "late" : "open"} />)}</div>;
}

export function Progress({ value }) {
  return <div className="progress"><span style={{ width: `${Math.min(Math.max(value, 0), 100)}%` }} /></div>;
}

// A filter that picks several values at once (stage, product, …) instead of
// one — a native <select> can't do that. Closes on an outside click or Esc;
// the trigger's own label tells you what's active without opening it
// ("All", one name, or "N selected"), so the filter stays legible collapsed.
export function CheckboxDropdown({ label, options, selected, onToggle, onSelectAll, onClear, className = "" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event) => { if (ref.current && !ref.current.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const allCount = options.length;
  const selectedCount = selected.size;
  const summary = selectedCount === 0 ? `${label} (none)`
    : selectedCount === allCount ? label
    : selectedCount === 1 ? options.find((option) => selected.has(option.value))?.label || label
    : `${label} (${selectedCount})`;

  return (
    <div className={`Checkbox-Dropdown ${className}`} ref={ref}>
      <button type="button" className={`Checkbox-Dropdown-Trigger${selectedCount !== allCount ? " active" : ""}`} onClick={() => setOpen((value) => !value)}>
        {summary}
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="Checkbox-Dropdown-Panel">
          <div className="Checkbox-Dropdown-Actions">
            <button type="button" onClick={onSelectAll}>Select all</button>
            <button type="button" onClick={onClear}>Clear</button>
          </div>
          {options.map((option) => (
            <label key={option.value} className="Checkbox-Dropdown-Option">
              <input type="checkbox" checked={selected.has(option.value)} onChange={() => onToggle(option.value)} />
              {option.color && <span className="Checkbox-Dropdown-Dot" style={{ background: option.color }} />}
              {option.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
